// ═══════════════════════════════════════════════════════════
// useRealtimeEvents — webhook-driven store refresh
//
// Polls /api/events/poll every 5s. The endpoint does a handful
// of KV reads and returns only the table IDs touched since the
// last poll. Store reloads fire only when something changed —
// zero unnecessary fetches in quiet periods.
//
// KV entries are written by /api/webhooks/gab whenever GAB
// fires a subscription event.
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';
import { useRequestStore } from '../store/useRequestStore';

const POLL_MS = 5_000;

// GAB table ID → logical action key
const TABLE_MAP: Record<string, string[]> = {
  'pa1l6s8qe': ['requests'],        // requests — new submission, status change, extension
  'fz6kyugqq': ['documents'],       // documents — file uploaded or redaction saved
  'z51jczl9a': ['correspondence'],  // correspondence — letter sent or received
  'i1sy9y14o': ['invoices'],        // invoices — created or payment received
  'ey1oawa36': ['tasks'],           // tasks — created, completed, reassigned
  'a5yjfudpv': ['requests'],        // auditLog changes often follow request updates
};

export function useRealtimeEvents() {
  const lastRef = useRef(new Date(Date.now() - 10_000).toISOString());
  const { refresh } = useRequestStore();

  useEffect(() => {
    const poll = async () => {
      try {
        const since = lastRef.current;
        lastRef.current = new Date().toISOString();

        const res = await fetch(`/api/events/poll?since=${encodeURIComponent(since)}`);
        if (!res.ok) return;
        const { changed } = (await res.json()) as { changed: string[] };
        if (changed.length === 0) return;

        const actions = new Set(changed.flatMap(tid => TABLE_MAP[tid] ?? []));

        // Requests store covers the main list + detail view (upsert pattern)
        if (actions.has('requests') || actions.has('documents') ||
            actions.has('correspondence') || actions.has('invoices') ||
            actions.has('tasks')) {
          refresh();
        }

        // Dispatch a custom event so open RequestDetail tabs can re-fetch
        // their own sub-resources (documents, correspondence, invoices, tasks)
        if (changed.length > 0) {
          window.dispatchEvent(new CustomEvent('foia:datachange', {
            detail: { tables: changed, actions: [...actions] },
          }));
        }
      } catch {
        // network error — silent, next tick retries
      }
    };

    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);
}
